import { memo, useMemo } from "react";

import { getURL } from "@/lib/aws-amplify/storage";
import type { LangCode } from "@/lib/gql/__generated__/graphql";

import type * as FormModelType from "@/components/features/design/plain/model/type";

import * as Shared from "./_shared";
import { ResourceCard } from "../../resource-card";
import { CardList } from "../card-list";

type Props = {
	rooms: FormModelType.QueryData["roomList"]["rooms"];
	fontFamilyClassName: string;
	styleColor: string;
	lang: LangCode;
};

export const RoomList = memo(({ rooms, fontFamilyClassName, styleColor, lang }: Props) => {
	const hasMore = useMemo(() => rooms.length > 2, [rooms.length]);
	const slicedRooms = useMemo(() => (hasMore ? rooms.slice(0, 3) : rooms), [hasMore, rooms]);

	const convertedRooms = useMemo(() => {
		return slicedRooms.map((room) => {
			const roomSize = room.roomSize ?? undefined;
			const roomSizeText = roomSize ? ` | ${roomSize}㎡` : "";
			const capacityPeopleMaximum = room.capacityPeopleMaximum ?? 0;
			const capacityText = `〜${capacityPeopleMaximum}${Shared.Constants.CapacityPrefix[lang]}`;
			const roomInfoText = `${capacityText}${roomSizeText}`;
			return {
				title: room.name,
				mainKeyVisualUrl: room.mainKeyVisual ? getURL(room.mainKeyVisual) : "",
				catchCopy: roomInfoText,
				priceLabel: Shared.Constants.LastSevenDaysStatement[lang],
				priceText: "￥XXXX〜",
				priceUnit: Shared.Constants.Person[lang],
			};
		});
	}, [lang, slicedRooms]);

	return (
		<CardList title="部屋一覧" hasMore={hasMore} styleColor={styleColor} fontFamilyClassName={fontFamilyClassName}>
			<div className="flex flex-col gap-6">
				{convertedRooms.map((room, index) => (
					<ResourceCard
						key={index}
						imageUrl={room.mainKeyVisualUrl}
						title={room.title}
						catchCopy={room.catchCopy}
						priceLabel={room.priceLabel}
						priceText={room.priceText}
						priceUnit={room.priceUnit}
					/>
				))}
			</div>
		</CardList>
	);
});
